import { Application } from 'pixi.js';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import {
  clearBackgroundRecordingApp,
  isPixiAppDestroyed,
  markBackgroundRecordingApp,
} from '../components/canvas/utils/backgroundRecordingState';

interface AudioData {
  src: string;
  name: string;
  ref: any;
}

interface CustomTimelineAction {
  id: string;
  start: number;
  end: number;
  effectId: string;
  data: AudioData;
}

export class BackgroundRecorder {
  private app: Application;
  private timelineState: TimelineState;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private originalTime: number;
  private recordingStartTime: number = 0;
  private audioSource: AudioBufferSourceNode | null = null;
  private chunkFlushTimer: number | null = null;
  private destroyAppAfterRecording: boolean = false;

  constructor(app: Application, timelineState: TimelineState) {
    this.app = app;
    this.timelineState = timelineState;
    this.originalTime = timelineState.getTime();
  }

  usesApp(app: Application): boolean {
    return this.app === app;
  }

  prepareForRouteCapture() {
    markBackgroundRecordingApp(this.app, true);
    this.destroyAppAfterRecording = true;
  }

  private cleanupRouteCapture() {
    // Always clear the marker: using `||` here would short-circuit and leave
    // the app marked for destruction, allowing a second cleanup to destroy it.
    const wasMarkedForDestroy = clearBackgroundRecordingApp(this.app);
    const shouldDestroy = this.destroyAppAfterRecording || wasMarkedForDestroy;
    this.destroyAppAfterRecording = false;

    if (shouldDestroy && !isPixiAppDestroyed(this.app)) {
      this.app.destroy(true, { children: true });
    }
  }

  cancelRouteCapture() {
    this.cleanupRouteCapture();
  }

  async recordInBackground(
    duration: number,
    audioBlob?: Blob,
    timelineData?: any
  ): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        let recordingAudioContext: AudioContext | null = null;
        try {
          recordingAudioContext = new (
            window.AudioContext || (window as any).webkitAudioContext
          )();
          if (recordingAudioContext.state === 'suspended') {
            await recordingAudioContext.resume();
          }
        } catch (ctxErr) {
          console.error('Unable to initialize recording audio context:', ctxErr);
        }

        const wasPlaying = this.timelineState.isPlaying;
        if (wasPlaying) {
          this.timelineState.pause();
        }

        const videoStream = this.app.canvas.captureStream(60);

        let combinedStream = videoStream;
        let audioStartTime = 0;
        let silentOscillator: OscillatorNode | null = null;
        let silentGain: GainNode | null = null;

        if (audioBlob && timelineData && recordingAudioContext) {
          try {
            const { earliestStart } = this.getAudioTimeRange(timelineData);
            audioStartTime = earliestStart !== null ? earliestStart : 0;

            const audioArrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer =
              await recordingAudioContext.decodeAudioData(audioArrayBuffer);

            const audioDestination =
              recordingAudioContext.createMediaStreamDestination();
            this.audioSource = recordingAudioContext.createBufferSource();
            this.audioSource.buffer = audioBuffer;
            this.audioSource.connect(audioDestination);

            combinedStream = new MediaStream([
              ...videoStream.getVideoTracks(),
              ...audioDestination.stream.getAudioTracks(),
            ]);

            console.log('Combined stream created with audio and video tracks');
          } catch (audioError) {
            console.error('Error setting up audio:', audioError);
          }
        } else if (recordingAudioContext) {
          try {
            const silentDestination =
              recordingAudioContext.createMediaStreamDestination();
            silentOscillator = recordingAudioContext.createOscillator();
            silentGain = recordingAudioContext.createGain();
            silentGain.gain.value = 0;
            silentOscillator.connect(silentGain);
            silentGain.connect(silentDestination);
            silentOscillator.start();

            combinedStream = new MediaStream([
              ...videoStream.getVideoTracks(),
              ...silentDestination.stream.getAudioTracks(),
            ]);
          } catch (silentAudioError) {
            console.error('Failed to add silent audio track:', silentAudioError);
          }
        }

        const hasAudioTrack = combinedStream.getAudioTracks().length > 0;
        let mimeType = 'video/webm';
        if (hasAudioTrack) {
          if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
            mimeType = 'video/webm;codecs=vp9,opus';
          } else if (
            MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ) {
            mimeType = 'video/webm;codecs=vp8,opus';
          }
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
          mimeType = 'video/webm;codecs=vp8';
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
          mimeType = 'video/webm;codecs=vp9';
        }

        this.mediaRecorder = new MediaRecorder(combinedStream, {
          mimeType: mimeType,
          videoBitsPerSecond: 5000000,
        });

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.chunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          if (this.chunkFlushTimer !== null) {
            window.clearInterval(this.chunkFlushTimer);
            this.chunkFlushTimer = null;
          }
          const blob = new Blob(this.chunks, { type: 'video/webm' });
          this.chunks = [];

          this.timelineState.pause();
          this.timelineState.setTime(this.originalTime);
          if (wasPlaying) {
            this.timelineState.play;
          }

          combinedStream.getTracks().forEach((track) => track.stop());

          if (this.audioSource) {
            try {
              this.audioSource.stop();
            } catch (e) {
              // Ignore if already stopped
            }
          }
          if (silentOscillator) {
            try {
              silentOscillator.stop();
            } catch (e) {
              // Ignore if already stopped
            }
          }
          silentOscillator?.disconnect();
          silentGain?.disconnect();
          if (
            recordingAudioContext &&
            recordingAudioContext.state !== 'closed'
          ) {
            recordingAudioContext.close().catch(() => {});
          }
          this.cleanupRouteCapture();

          resolve(blob);
        };

        this.mediaRecorder.start(250);
        this.recordingStartTime = Date.now();
        this.chunkFlushTimer = window.setInterval(() => {
          if (this.mediaRecorder?.state === 'recording') {
            try {
              this.mediaRecorder.requestData();
            } catch {
              // ignore intermittent requestData failures
            }
          }
        }, 300);

        this.timelineState.setTime(0);
        this.timelineState.reRender();
        let audioStarted = false;

        this.renderFrame(Math.max(1, duration), audioStartTime, audioStarted);
      } catch (error) {
        this.cleanupRouteCapture();
        reject(error);
      }
    });
  }

  private renderFrame(
    totalDuration: number,
    audioStartTime: number,
    audioStarted: boolean
  ) {
    if (!this.mediaRecorder) return;

    const currentTime = (Date.now() - this.recordingStartTime) / 1000; // Current time in seconds

    // Start audio at the appropriate time
    if (!audioStarted && this.audioSource && currentTime >= audioStartTime) {
      try {
        this.audioSource.start();
        audioStarted = true;
        console.log(`Started audio playback at ${currentTime}s`);
      } catch (e) {
        console.error('Error starting audio:', e);
      }
    }

    // Update and render timeline-controlled assets for the captured frame.
    this.timelineState.setTime(currentTime);
    this.timelineState.reRender();

    if (currentTime >= totalDuration) {
      try {
        this.mediaRecorder.requestData();
      } catch (e) {
        // requestData may throw if recorder is inactive; safe to ignore.
      }
      this.mediaRecorder.stop();
      return;
    }

    // Schedule next frame
    requestAnimationFrame(() =>
      this.renderFrame(totalDuration, audioStartTime, audioStarted)
    );
  }

  /**
   * Helper function that extracts start and end times of audio segments from timeline data
   */
  private getAudioTimeRange(segments: any[]) {
    let earliestStart = Infinity;
    let latestEnd = -Infinity;

    segments.forEach((segment) => {
      segment.actions.forEach((action: CustomTimelineAction) => {
        if (action.id.startsWith('audio-action')) {
          if (action.start < earliestStart) {
            earliestStart = action.start;
          }

          if (action.end > latestEnd) {
            latestEnd = action.end;
          }
        }
      });
    });

    if (earliestStart === Infinity || latestEnd === -Infinity) {
      return { earliestStart: null, latestEnd: null };
    }

    return { earliestStart, latestEnd };
  }
}
